import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";

export default function StorybankPage() {
  const confirm = useConfirm();
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", raw: "", tags: "", kind: "story", source_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/stories");
    setStories(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };
  useEffect(()=>{ load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/stories", {
        title: form.title,
        raw: form.raw,
        kind: form.kind,
        source_url: form.source_url,
        tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean),
      });
      toast.success(form.kind === "podcast_link" ? "Podcast link saved." : "Story captured, arc extracted from your words.");
      setForm({ title:"", raw:"", tags:"", kind:"story", source_url:"" });
      setShowAdd(false);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    const st = stories.find(x => x.id === id);
    const ok = await confirm({
      title: "Delete this story?",
      description: `"${st?.title || 'This story'}" will be moved to Trash. You can restore it anytime.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/stories/${id}`);
    toast.success("Moved to Trash. Restore anytime.");
    setSelected(null);
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Storybank"
        title="Your raw, true material."
        subtitle="Real moments, not generic filler. She'll ground drafts in these stories and preserve your original wording where possible."
        action={<button data-testid="story-add-btn" className="btn btn-accent" onClick={()=>setShowAdd(true)}><Plus size={16}/> Capture story</button>}
      />

      <div className="mb-6 p-4 rounded-2xl flex items-start gap-3" style={{background:'var(--accent-tint)', border:'1px solid var(--accent)'}} data-testid="capture-on-the-go-note">
        <span className="text-lg leading-none mt-0.5">✨</span>
        <div className="text-sm" style={{color:'var(--ink-1)'}}>
          <span className="font-medium">Capture on the go — coming soon.</span>{" "}
          <span style={{color:'var(--ink-2)'}}>Forward a voice note over WhatsApp or an email to your Storybank inbox and she'll transcribe it, extract the arc, and file it here automatically.</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 card p-4 max-h-[70vh] overflow-auto scroll-thin">
          <div className="eyebrow px-2 py-1">{stories.length} stories</div>
          {stories.length === 0 ? (
            <div className="p-6 text-sm" style={{color:'var(--ink-3)'}}>Capture your first story, a moment that surprised you, a client that pushed back, a lesson you learned.</div>
          ) : stories.map(s => (
            <button key={s.id} data-testid={`story-item-${s.id}`} onClick={()=>setSelected(s)}
              className={`w-full text-left px-3 py-3 rounded-2xl fade-up mb-1 transition-colors`}
              style={selected?.id===s.id
                ? { background: 'var(--accent-tint)', border: '1px solid var(--accent)' }
                : { background: 'var(--bg-soft)', border: '1px solid transparent' }}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm truncate">{s.title}</div>
                <span className="text-[10px] font-mono opacity-70 shrink-0">{new Date(s.created_at).toLocaleDateString(undefined, { month:'short', day:'numeric' })}</span>
              </div>
              <div className="text-xs mt-1 opacity-70 line-clamp-2">{s.raw ? s.raw.slice(0,120) : (s.source_url || "-")}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8 card p-6">
          {!selected ? (
            <div className="py-16 text-center">
              <div className="eyebrow mb-2">Nothing selected</div>
              <p className="font-serif text-3xl">Pick a story, or capture a new one.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="eyebrow mb-1">{
                    selected.kind === "podcast_link" ? "Podcast link" :
                    selected.kind === "podcast_transcript" ? "Podcast transcript" :
                    selected.kind === "professional_story" ? "Professional story" :
                    selected.kind === "case_study" ? "Case study" :
                    selected.kind === "lesson_learned" ? "Lesson learned" :
                    selected.kind === "career_milestone" ? "Career milestone" :
                    selected.kind === "failure" ? "Professional failure" :
                    selected.kind === "behind_the_scenes" ? "Behind the scenes" :
                    selected.kind === "opinion" ? "Opinion" :
                    selected.kind === "interview" ? "Interview / talk" :
                    selected.kind === "note" ? "Note" : "Story"
                  }</div>
                  <h2 className="font-serif text-3xl tracking-tight" data-testid="story-title">{selected.title}</h2>
                  {selected.source_url && <a href={selected.source_url} target="_blank" rel="noreferrer" className="text-xs link-underline font-mono mt-1 inline-block" style={{color:'var(--accent)'}}>{selected.source_url}</a>}
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {selected.tags?.map((t,i)=><span key={i} className="chip">{t}</span>)}
                  </div>
                </div>
                <button onClick={()=>remove(selected.id)} data-testid={`story-delete-${selected.id}`} className="btn btn-ghost text-xs"><Trash size={14}/> Delete</button>
              </div>

              {selected.kind === "podcast_link" ? (
                <div className="p-6 rounded-2xl text-sm text-center" style={{background:'var(--bg-soft)', border: "1px solid var(--line)", color:'var(--ink-2)'}}>
                  Podcast reference saved. Paste a transcript later to have her extract the arc.
                </div>
              ) : (
                <>
                  <div className="eyebrow mb-2">{selected.kind === "podcast_transcript" ? "Transcript (preserved verbatim)" : "Raw (preserved verbatim)"}</div>
                  <div className="p-4 rounded-2xl mb-6 whitespace-pre-wrap text-sm max-h-[280px] overflow-auto scroll-thin" style={{background:'var(--bg-soft)', border: "1px solid var(--line)"}} data-testid="story-raw">
                    {selected.raw}
                  </div>

                  <div className="eyebrow mb-3">Narrative arc</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {["struggle","depth","solution","transformation","success"].map(k => (
                      <div key={k} className="p-4 rounded-2xl" style={{ background: "var(--bg-soft)", border: "1px solid var(--line)" }}>
                        <div className="eyebrow mb-2" style={{color:'var(--accent)'}}>{k}</div>
                        <div className="text-sm" data-testid={`story-arc-${k}`}>{selected.arc?.[k] || <span style={{color:'var(--ink-3)'}}>-</span>}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setShowAdd(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="card p-6 w-full max-w-2xl" data-testid="story-add-form">
            <div className="eyebrow mb-2">New entry</div>
            <h3 className="font-serif text-3xl mb-6">Give her material.</h3>

            <label className="block mb-4">
              <div className="eyebrow mb-2">Kind</div>
              <select data-testid="story-form-kind" className="select" value={form.kind} onChange={e=>setForm({...form, kind:e.target.value})}>
                <optgroup label="Personal">
                  <option value="story">Personal story</option>
                  <option value="note">Note / other</option>
                </optgroup>
                <optgroup label="Professional">
                  <option value="professional_story">Professional story</option>
                  <option value="case_study">Case study / client win</option>
                  <option value="lesson_learned">Lesson learned at work</option>
                  <option value="career_milestone">Career milestone</option>
                  <option value="failure">Professional failure</option>
                  <option value="behind_the_scenes">Behind the scenes</option>
                  <option value="opinion">Opinion / hot take</option>
                </optgroup>
                <optgroup label="Content sources">
                  <option value="podcast_transcript">Podcast transcript</option>
                  <option value="podcast_link">Podcast link</option>
                  <option value="interview">Interview / talk</option>
                </optgroup>
              </select>
            </label>

            <label className="block mb-4">
              <div className="eyebrow mb-2">Title</div>
              <input required data-testid="story-form-title" className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder={form.kind==='podcast_link' ? "Ep. 42, Positioning with Chris Do" : "The client who fired us in month two"} />
            </label>

            {form.kind === "podcast_link" ? (
              <label className="block mb-4">
                <div className="eyebrow mb-2">Podcast URL</div>
                <input required data-testid="story-form-url" className="input" value={form.source_url} onChange={e=>setForm({...form, source_url:e.target.value})} placeholder="https://podcasts.apple.com/…" />
              </label>
            ) : (
              <label className="block mb-4">
                <div className="eyebrow mb-2">{form.kind === "podcast_transcript" ? "Transcript (paste the full text)" : "Raw story (in your words)"}</div>
                <textarea required data-testid="story-form-raw" className="textarea min-h-[220px]" value={form.raw} onChange={e=>setForm({...form, raw:e.target.value})} placeholder={form.kind === "podcast_transcript" ? "Paste the podcast transcript here…" : "Write it the way you'd tell a friend at a bar. Facts, feelings, dialogue."} />
              </label>
            )}

            <label className="block mb-6">
              <div className="eyebrow mb-2">Tags</div>
              <input data-testid="story-form-tags" className="input" value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})} placeholder="failure, lesson, clients" />
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button>
              <button disabled={busy} data-testid="story-form-submit" className="btn btn-accent">{busy?"Extracting arc…":"Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
